import { useTranslation } from "react-i18next";
import { useMenuStations } from "../../../../hooks/useMenu";
import { getTranslation } from "../../../../utils/translations";
import { stationColorOf } from "./catalogColors";
import { ONE_CHIP_ROW, useChipRows } from "./useChipRows";
import { ExpandRowsButton } from "./ExpandRowsButton";
import { CHIP_GRID, ColorChip } from "./ColorChip";
import { HomeChip } from "./HomeChip";

interface StationChipsProps {
  selectedStationId: number | null;
  onStationSelect: (stationId: number | null) => void;
}

/**
 * Редът със СТАНЦИИ над каталога (настройка на ресторанта `catalogFilter` =
 * "stations"; при "groups" тук стои CategoryChips, а станциите отиват над
 * прегледа на поръчката). Плосък списък — станциите нямат дърво: къщичка =
 * всички, chip = филтър по станция с точка в цвета ѝ (ColorChip dot).
 * Свиването/„Всички" е общо с групите (useChipRows). Само на десктоп.
 */
export const StationChips = ({
  selectedStationId,
  onStationSelect,
}: StationChipsProps) => {
  const { t, i18n } = useTranslation();
  const stations = useMenuStations();
  const sorted = [...stations].sort(
    (a, b) =>
      (a.sortIndex ?? Number.MAX_SAFE_INTEGER) -
      (b.sortIndex ?? Number.MAX_SAFE_INTEGER),
  );
  const { rowClipRef, rowOverflows, showAllRows, toggleShowAllRows } =
    useChipRows("topFilterExpanded", ONE_CHIP_ROW, [stations]);

  return (
    <div className="px-5 py-3 border-b border-pos-line2 bg-pos-panel">
      <div className="flex items-start gap-3">
        <div
          ref={rowClipRef}
          className={`flex-1 min-w-0 ${showAllRows ? "" : "max-h-10 overflow-hidden"}`}
        >
          {/* Равни колони (CHIP_GRID); къщичката е първата клетка */}
          <div className={CHIP_GRID}>
            <HomeChip
              title={t("ordering.allStations")}
              onClick={() => onStationSelect(null)}
            />
            {sorted.map((station) => {
              const active = station.id === selectedStationId;
              return (
                <ColorChip
                  key={station.id}
                  color={stationColorOf(station)}
                  dot
                  active={active}
                  onClick={() => onStationSelect(active ? null : station.id)}
                >
                  {getTranslation(
                    station.translations,
                    i18n.language,
                    station.slug,
                  )}
                </ColorChip>
              );
            })}
          </div>
        </div>
        {rowOverflows && (
          <ExpandRowsButton
            expanded={showAllRows}
            onToggle={toggleShowAllRows}
          />
        )}
      </div>
    </div>
  );
};
